import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Trans, t } from '@lingui/macro';
import ReactGa from 'react-ga';
import classNames from 'classnames';
import css from './footer-qtc.scss';
import { BtnText } from '../../components/buttons';
import {
  markActivity,
  resetConquest,
  setNextQuestion
} from '../conquest/conquest.actions';
import InfoFeedback from '../info-feedback/info-feedback';
import Info from '../info/info';
import { getValueFromUrl } from '../../helpers/utils';
import { ModalGeneric } from '../../components/modal-content';
import { constructModal, activateModal } from '../modal/modal-actions';
import { useLocalisation } from '../../helpers/i18n/use-localisation';
import { handInQtc } from '../mle/mle-actions';
import { enableSpinner, disableSpinner } from '../spinner/spinner-actions';
import { durationTypeCssClass, DURATION_TYPE_WHOLE, LEARNOSITY_SPINNER } from '../../helpers/constants';

const FooterQtc = () => {
  const {
    isLoading,
    activityIndex,
    conquestIsReady,
    conquestUserAnswer,
    girlId
  } = useSelector(state => state.conquestReducer);
  const {
    questions,
    isCompleted,
    durationType
  } = useSelector(state => state.mleReducer || {});
  const dispatch = useDispatch();
  const { i18n } = useLocalisation();

  const handInModal = () => {
    const modalData = {
      subHeader: i18n._(t`Have you reviewed your answers?`),
      paragraph: i18n._(t`You can open this assessment to review and edit answers until the time is up.
      When the time is up your assessment will be handed in.`),
      buttons: [
        {
          text: i18n._(t`Go back`),
          callBack: () => dispatch(activateModal(false)),
          size: 'large',
          type: 'secondary'
        },
        {
          text: i18n._(t`I'm ready!`),
          callBack: () => dispatch(handInQtc()),
          size: 'large'
        }
      ]
    };
    dispatch(constructModal({
      activate: true,
      content: <ModalGeneric modalData={modalData} />,
      canvasOnClick: false
    }));
  };

  const handleSaveAnswer = async () => {
    dispatch(enableSpinner(LEARNOSITY_SPINNER));
    await dispatch(markActivity({
      assignmentId: getValueFromUrl('assignmentId'),
      questionId: girlId,
      studentAnswer: conquestUserAnswer
    }));
    dispatch(setNextQuestion({
      currentIndex: activityIndex,
      totalQuestions: questions.length
    }));
    ReactGa.event({
      category: 'Button - Save answer',
      action: 'Mark the question',
      label: 'Student interaction'
    });
  };

  const handleChangeAnswer = async () => {
    dispatch(enableSpinner(LEARNOSITY_SPINNER));
    await dispatch(markActivity({
      assignmentId: getValueFromUrl('assignmentId'),
      questionId: girlId,
      studentAnswer: null
    }));
    dispatch(resetConquest());
    dispatch(disableSpinner(LEARNOSITY_SPINNER));
    ReactGa.event({
      category: 'Button - Change Answer',
      action: 'Change answer',
      label: 'Student interaction'
    });
  };

  const keyUpHandler = (event) => {
    if (conquestIsReady && event.key === 'Enter') {
      handleSaveAnswer();
    }
  };

  useEffect(() => {
    if (durationType === DURATION_TYPE_WHOLE) {
      window.addEventListener('keyup', keyUpHandler);
    }
    return () => {
      if (durationType === DURATION_TYPE_WHOLE) {
        window.removeEventListener('keyup', keyUpHandler);
      }
    };
  }, [conquestIsReady, conquestUserAnswer, isLoading]);

  return (
    <div className={classNames(css.footer, css[durationTypeCssClass[durationType]])}>
      <div className={css['activity-side']}>
        <div className={css['misc-container']}>
          <Info />
          <InfoFeedback
            className={css['info-feedback-margin']}
            girlId={girlId || ''}
          />
        </div>
        <div className={css['btn-cont']}>
          {
            questions.length > 0 && questions[activityIndex].isAttempted ?
              (
                <BtnText
                  className={css.btn}
                  callBack={handleChangeAnswer}
                  isDisabled={isLoading || isCompleted}
                  type="secondary"
                >
                  <Trans>Change answer</Trans>
                </BtnText>
              ) : (
                <BtnText
                  className={css.btn}
                  callBack={handleSaveAnswer}
                  isDisabled={!conquestIsReady || isCompleted}
                  type="secondary"
                >
                  <Trans>Save answer</Trans>
                </BtnText>
              )
          }
        </div>
      </div>
      <div className={css['progress-side']}>
        <BtnText
          className={css.btn}
          callBack={handInModal}
          isDisabled={isLoading || isCompleted}
        >
          <Trans>Hand in</Trans>
        </BtnText>
      </div>
    </div>
  );
};

export default FooterQtc;

